import { cssJoin } from '@volvo-cars/css/utils';
import type React from 'react';
import {
  type ChangeEvent,
  type CSSProperties,
  forwardRef,
  useCallback,
  useRef,
  useState,
} from 'react';
import type { GlobalHTMLAttributes } from './types';
import { useIds } from './use-ids';

export interface DoubleRangeSliderProps extends GlobalHTMLAttributes {
  /**
   * The name of the input to use when submitting the form.
   */
  name?: string;

  /**
   * Id of a form element that this input should be associated with. Defaults to the containing
   * form element.
   */
  form?: string;

  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>;

  /**
   * The minimum value for the input.
   * @default 0
   */
  min?: number;

  /**
   * The maximum value for the input.
   * @default 100
   */
  max?: number;

  /**
   * The step interval between values.
   * @default 1
   */
  step?: number;

  /**
   * Id of a `<datalist>` element with a list of predefined values to suggest to the user.
   */
  list?: string;

  /**
   * Default value of an uncontrolled input.
   */
  defaultValue?: [number, number];

  /**
   * Number of decimal places to round values to.
   */
  precision?: number;

  /**
   * Provide a tooltip while sliding, indicating the current value.
   */
  tooltip?: boolean;

  /**
   * Value of the input.
   *
   * Makes the input controlled.
   */
  value?: [number, number];

  /**
   * Native change event handler, passed through as-is from whichever input fired.
   *
   * Use this when you need access to the underlying DOM event (e.g. to inspect
   * `event.target.name` or integrate with form libraries).
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  /**
   * Fires immediately when either slider's value changes.
   *
   * Provides the full numeric pair `[min, max]` as the primary payload so
   * consumers can keep the range value in state without parsing native events.
   */
  onValueChange?: (values: [number, number]) => void;

  /**
   * Accessible label for the minimum value input.
   * @default "Minimum value"
   */
  minLabel?: string;

  /**
   * Accessible label for the maximum value input.
   * @default "Maximum value"
   */
  maxLabel?: string;

  /**
   * ID of an element that labels the slider group.
   */
  'aria-labelledby'?: string;

  /**
   * Accessible label for the slider group.
   */
  'aria-label'?: string;
}

type DoubleRangeStyle = CSSProperties & {
  '--range-value-min': number;
  '--range-value-max': number;
};

type RangeInputStyle = CSSProperties & {
  '--ratio': number;
  '--fill': number;
};

/**
 * `DoubleRangeSlider` renders two range inputs allowing selection of a min/max range.
 *
 * Can be used uncontrolled (native), or controlled. To make it controlled,
 * set both the `value` prop and the `onChange` handler.
 */
export const DoubleRangeSlider = forwardRef<
  HTMLDivElement,
  DoubleRangeSliderProps
>(function DoubleRangeSlider(
  {
    min = 0,
    max = 100,
    step = 1,
    value,
    defaultValue,
    onChange,
    onValueChange,
    tooltip = false,
    className,
    id,
    style,
    name,
    minLabel = 'Minimum value',
    maxLabel = 'Maximum value',
    'aria-labelledby': ariaLabelledBy,
    'aria-label': ariaLabel,
    ...props
  },
  ref,
) {
  const { rootId: sliderId } = useIds(id);

  const [innerValue, setInnerValue] = useState<[number, number]>(
    value ?? defaultValue ?? [min, max],
  );

  const activeInputRef = useRef<('min' | 'max') | null>(null);
  const currentValue = typeof value !== 'undefined' ? value : innerValue;

  const [currentMinValue, currentMaxValue] = Array.isArray(currentValue)
    ? currentValue
    : [min, max];

  const roundMethod = activeInputRef.current === 'max' ? 'floor' : 'ceil';

  const mid =
    currentMinValue +
    Math[roundMethod]((currentMaxValue - currentMinValue) / 2 / step) * step;

  const minFill = (currentMinValue - min) / (mid - min);
  const maxFill = (currentMaxValue - mid) / (max - mid);

  const minName = name ? `${name}-min` : 'min';
  const maxName = name ? `${name}-max` : 'max';

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const isMin = event.target.name === minName;
      activeInputRef.current = isMin ? 'min' : 'max';
      const newValue = Number(event.target.value);

      const nextValues: [number, number] = isMin
        ? [Math.min(newValue, currentMaxValue), currentMaxValue]
        : [currentMinValue, Math.max(newValue, currentMinValue)];

      if (typeof value === 'undefined') {
        setInnerValue(nextValues);
      }

      onChange?.(event);
      onValueChange?.(nextValues);
    },
    [onChange, onValueChange, value, currentMaxValue, currentMinValue, minName],
  );
  const doubleRangeStyle: DoubleRangeStyle = {
    ...style,
    '--range-value-min': (currentMinValue - min) / (max - min),
    '--range-value-max': (currentMaxValue - min) / (max - min),
  };
  const minInputStyle: RangeInputStyle = {
    '--ratio': (mid - min) / (max - min),
    '--fill': minFill,
  };
  const maxInputStyle: RangeInputStyle = {
    '--ratio': (max - mid) / (max - min),
    '--fill': maxFill,
  };

  return (
    <div
      id={id}
      ref={ref}
      role="group"
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
      className={cssJoin('double-range', className)}
      style={doubleRangeStyle}
      {...props}
    >
      <input
        id={`${sliderId}-min`}
        name={minName}
        min={min}
        max={mid}
        step={step}
        value={currentMinValue}
        onChange={handleChange}
        style={minInputStyle}
        type="range"
        aria-label={minLabel}
        className="range"
      />
      {tooltip && (
        <output
          htmlFor={`${sliderId}-min`}
          aria-live="polite"
          className="tooltip"
        >
          {currentMinValue}
        </output>
      )}
      <input
        id={`${sliderId}-max`}
        name={maxName}
        min={mid}
        max={max}
        step={step}
        value={currentMaxValue}
        onChange={handleChange}
        style={maxInputStyle}
        type="range"
        aria-label={maxLabel}
        className="range"
      />
      {tooltip && (
        <output
          htmlFor={`${sliderId}-max`}
          aria-live="polite"
          className="tooltip"
        >
          {currentMaxValue}
        </output>
      )}
    </div>
  );
});
